import { useDispatch, useSelector } from "react-redux";
import { ReduxRootState } from "../../../types/redux-store";
import { bulkModalNotice, bulkModelKey } from "./bulk-create-post.model";

/**
 * Inline alerts surfacing bulk-create problems: a capacity limit (e.g. "you can
 * duplicate up to 20 posts at a time") and an account that could not be
 * resolved. Sticky inside the modal rather than toasted, because a toast is
 * gone in a few seconds and the user is left with a form that dropped some of
 * their input and no way to rediscover why.
 *
 * The two are rendered and dismissed separately: they are unrelated problems
 * that can occur together, and clearing one must not remove the other's
 * explanation.
 */
const NoticeRow = ({
  message,
  onDismiss,
  testId,
}: {
  message: string;
  onDismiss: () => void;
  testId: string;
}) => (
  <div
    // `role="alert"` (assertive) rather than `role="status"` (polite):
    // this banner appears in direct response to a user action that
    // dropped some of their input (e.g. pasting more than the cap).
    // Polite would queue the announcement behind whatever the SR was
    // saying and risk the user missing it.
    role="alert"
    className="bg-primary-50 text-primary-600 text-sm px-3 py-2 mx-4 mt-3 rounded-lg flex items-start gap-2"
    data-testid={testId}
  >
    <i className="fa-regular fa-circle-info mt-0.5" aria-hidden="true" />
    <span className="flex-1">{message}</span>
    <button
      type="button"
      className="text-primary-600 hover:text-primary-700 cursor-pointer p-0 m-0"
      onClick={onDismiss}
      aria-label="Dismiss notice"
    >
      <i className="fa-solid fa-xmark" aria-hidden="true" />
    </button>
  </div>
);

export const BulkCapacityNotice = () => {
  const dispatch = useDispatch();
  const notice = useSelector(
    (state: ReduxRootState) => state[bulkModelKey]?.bulkModalNotice
  );
  const capacityMessage = notice?.capacityMessage ?? null;
  const accountMessage = notice?.accountMessage ?? null;

  if (!capacityMessage && !accountMessage) return null;

  return (
    <>
      {capacityMessage && (
        <NoticeRow
          message={capacityMessage}
          testId="bulk-capacity-notice"
          onDismiss={() =>
            dispatch(bulkModalNotice.actions.clearCapacityMessage())
          }
        />
      )}
      {accountMessage && (
        <NoticeRow
          message={accountMessage}
          testId="bulk-account-notice"
          onDismiss={() =>
            dispatch(bulkModalNotice.actions.clearAccountMessage())
          }
        />
      )}
    </>
  );
};
